import { Component } from '../Component.js';
/**
 * @typedef {Object} NavigationOptions
 * @property {{ getJSON: (key:string)=>any, setJSON:(key:string, value:any)=>void }} [storage]
 * @property {{ subscribe:(event:string, handler:Function)=>void }} [events]
 * @property {Array<{ id: string, title: string, path: string, isActive?: boolean }>} [items]
 */
import { SERVER_URL, USER_WORKER_URL, isSfwSite } from '../../js/config.js';
import { initOAuthManager } from '../../js/utils/oauthManager.js';
import { isGoogleOneTapAuthPending } from '../../js/utils/googleOneTap.js';
import { getCurrentLanguage, applyTranslations, normalizeLang, applyTranslationsToContainer, ensureNavTranslationsLoaded } from '../../js/translations.js';
import loginModalController from '../../js/components/LoginModalController.js';
import { tokenManager } from '../../js/utils/TokenManager.js';
import { storage } from '../../js/utils/storage.js';
import { resolveUserAvatarSrc } from '../../js/utils/userAvatar.js';
import { getAnalyticsTracker, initializeHangoutAnalytics } from '../../js/analytics/hangout-obs-tracker.js';
import { URLParamHandler } from '../../js/utils/urlParams.js';

// Navigation sub-modules
import { createEnergyBar, createSideNavEnergy, shouldShowEnergyBar } from './energy.js';
import { createFlagElement as _createFlagElementImpl, setupLazyFlagsLoading, ensureFlagsSpriteLoaded } from './flags.js';
import { createLanguageDropdown, getLanguageFlag, getLanguageName, LANGUAGE_DATA } from './language.js';
import { createNotificationIcon, updateNotificationCount, fetchNotificationCount, showNotificationPanel } from './notifications.js';
import { createTrialOfferButton, addTrialButtonEventListeners, showTrialOfferToastIfNeeded } from './trialOffer.js';
import { createFeedbackButton, handleFeedbackClick } from './feedback.js';
import { navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery } from './hangoutActions.js';
import { setupSideNavSwipe } from './sideNavSwipe.js';
import { seoPathForLanguage } from '../../js/seoLocalePath.js';
import {
  OPENROUTER_KEY_STATUS_EVENT,
  clearOpenRouterKeyStatusCache,
  refreshOpenRouterKeyStatus
} from '../../js/utils/openRouterKeyStatus.js';

export class Navigation extends Component {
  /** @param {string} path */
  _normalizePath(path) {
    if (!path) return '';
    const base = window.location.origin;
    try {
      const url = new URL(path, base);
      return (url.pathname || '').replace(/\/+$/, '') || '/';
    } catch {
      return String(path).split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    }
  }

  _isActiveNavPath(itemPath) {
    if (!itemPath || typeof itemPath !== 'string') return false;
    if (/^https?:\/\//i.test(itemPath)) return false;

    const current = this._normalizePath(window.location.pathname);
    const target = this._normalizePath(itemPath);

    let currentNoHtml = current.replace(/\.html$/i, '');
    const targetNoHtml = target.replace(/\.html$/i, '');

    if (currentNoHtml === '/' || currentNoHtml === '/index') currentNoHtml = '/rankings';

    return currentNoHtml === targetNoHtml;
  }

  get defaultOptions() {
    return {
      googleAuthUrl: `${SERVER_URL}/api/v1/auth/google/redirect`,
      loginEndpoint: '/api/v1/login',
      discordInviteUrl: 'https://discord.gg/S2Ez7UEvkc',
      items: [
        { id: 'home', title: 'nav.home', path: '/rankings.html', icon: 'fa-home', iconType: 'fontawesome', isActive: false },
        { id: 'season_pass', title: 'nav.season_pass', path: '/premium-pass.html', icon: 'fa-crown', iconType: 'fontawesome', isActive: false },
        { id: 'theatre', title: 'nav.theatre', path: '/create-scenario.html', icon: 'fa-pen-fancy', iconType: 'fontawesome', isActive: false },
        { id: 'create', title: 'nav.create', path: '/create-character.html', icon: 'fa-user-plus', iconType: 'fontawesome', isActive: false },
        { id: 'create_video', title: 'nav.create_video', path: '/create-video.html', icon: 'fa-photo-film', iconType: 'fontawesome', isActive: false },
        { id: 'my_gallery', title: 'nav.my_gallery', path: '/my-gallery.html', icon: 'fa-images', iconType: 'fontawesome', isActive: false },
        { id: 'updates', title: 'nav.updates', path: '/updates.html', icon: 'fa-newspaper', iconType: 'fontawesome', isActive: false },
        { id: 'account', title: 'nav.account', path: '/account.html', icon: 'fa-user-circle', iconType: 'fontawesome', isActive: false },
        { id: 'pricing', title: 'nav.pricing', path: '/pricing.html', icon: 'fa-tags', iconType: 'fontawesome', isActive: false }//,
        // { id: 'discord', title: 'nav.discord', path: 'https://discord.gg', icon: 'fa-brands fa-discord', iconType: 'fontawesome', isActive: false }
      ]
    };
  }

  async init() {
    if (this._initStarted) return;
    this._initStarted = true;
    this.language = getCurrentLanguage();
    this.userData = null;
    this._energyCleanups = [];

    initializeHangoutAnalytics();
    getAnalyticsTracker().trackSessionStart();

    try {
      this.oauthManager = initOAuthManager();
      window.oauthManager = this.oauthManager;
    } catch (error) {
      console.error('[Navigation] Init failed:', error);
    }

    await this._loadUserData();
    this._setupEventListeners();

    this.render();

    if (this.userData?.isLoggedIn) {
      void refreshOpenRouterKeyStatus();
      setTimeout(async () => {
        await this._updateNotificationBadge();
        setInterval(() => this._updateNotificationBadge(), 120000);
      }, 100);
    }

    this._setupMobileAutoHideNav();

    const navContainer = document.getElementById('side-navigation-container');
    setupLazyFlagsLoading(navContainer);

    URLParamHandler.processParams();
  }

  _setupEventListeners() {
    document.addEventListener('auth:login:success', this._handleAuthSuccess.bind(this));
    document.addEventListener('auth:logout', this._handleAuthLogout.bind(this));
    document.addEventListener('auth:token:expired', this._handleAuthLogout.bind(this));
    document.addEventListener('usernameUpdated', this._handleUsernameUpdated.bind(this));
    document.addEventListener('membershipDataSynced', this._handleMembershipDataSynced.bind(this));
    document.addEventListener(OPENROUTER_KEY_STATUS_EVENT, this._handleOpenRouterKeyStatus.bind(this));
    document.addEventListener('languageChanged', (e) => {
      if (e.detail?.language) {
        this.language = e.detail.language;
        this.render();
      }
    });
  }

  _isHangoutPage() {
    const pathname = window.location.pathname;
    return pathname.includes('/hangout') || pathname.includes('hangout.html');
  }

  async _loadUserData() {
    try {
      const token = await tokenManager.getValidAccessToken();

      if (token) {
        const userData = storage.get('userData');
        if (userData) {
          this.userData = { ...userData, isLoggedIn: true };
          storage.updateJson('userData', 'isLoggedIn', true);
          if (userData.language) this.setLanguage(userData.language);
          return;
        }

        const opts = /** @type {NavigationOptions & any} */ (this.options);
        if (opts.storage) {
          const userData = opts.storage.getJSON('user');
          if (userData) {
            this.userData = { ...userData, isLoggedIn: true };
            storage.updateJson('userData', 'isLoggedIn', true);
            if (userData.language) this.setLanguage(userData.language);
            return;
          }
        }

        const existingUserData = this._getExistingUserData();
        this.userData = {
          ...existingUserData,
          isLoggedIn: true,
          name: existingUserData.name || '',
          avatar: existingUserData.avatar || null,
          _usernameLoaded: existingUserData._usernameLoaded || false
        };
        this._saveUserData(this.userData);
        return;
      } else {
        const userData = storage.get('userData');
        if (userData) {
          userData.isLoggedIn = false;
          this.userData = userData;
          if (userData.language) this.setLanguage(userData.language);
        } else {
          this.userData = null;
        }
      }
    } catch (error) {
      console.error('[Navigation] Error loading user data:', error);
      this.userData = null;
    }
  }

  _getExistingUserData() {
    try {
      return storage.get('userData') || {};
    } catch {
      return {};
    }
  }

  _saveUserData(data) {
    storage.set('userData', data);
    const opts = /** @type {NavigationOptions & any} */ (this.options);
    if (opts.storage) opts.storage.setJSON('user', data);
  }

  async _storeHangoutSessionIfNeeded() {
    if (!this._isHangoutPage()) return;

    try {
      const [
        { storageManager },
        { HangoutState }
      ] = await Promise.all([
        import('../../core/storage/index.js'),
        import('../../js/hangout/state/HangoutState.js')
      ]);

      const hangoutConfig = await storageManager.getItem('currentHangoutConfig');

      if (hangoutConfig) {
        const hangoutState = HangoutState.getInstance();
        const hangoutSessionState = {
          ...hangoutConfig,
          returnToHangout: true,
          returnUrl: window.location.href,
          conversationId: hangoutState.getConversationId(),
          timestamp: Date.now()
        };

        storage.set('hangoutSessionBeforeLogin', hangoutSessionState);
        console.log('[Navigation] Stored hangout session before login:', hangoutSessionState);
      }
    } catch (error) {
      console.error('[Navigation] Failed to store hangout session:', error);
    }
  }

  async _handleAuthSuccess(event) {
    if (!event.detail?.user) return;

    const userData = { ...event.detail.user, isLoggedIn: true };
    this.userData = { ...this._getExistingUserData(), ...userData };
    await this._fetchUsernameAfterLogin();
    this._saveUserData(this.userData);
    this.render();
    if (event.detail?.authTrigger !== 'session_restore') {
      void refreshOpenRouterKeyStatus();
    }
  }

  _handleAuthLogout() {
    this.userData = { isLoggedIn: false };
    clearOpenRouterKeyStatusCache();
    this.render();
  }

  _handleOpenRouterKeyStatus() {
    this.render();
  }

  async _fetchUsernameAfterLogin() {
    try {
      const { createUsernameService } = await import('../../js/services/usernameService.js');
      const usernameService = createUsernameService();
      const usernameInfo = await usernameService.getUsernameInfo();

      if (usernameInfo.success && usernameInfo.username) {
        this.userData.name = usernameInfo.username;
        this.userData._usernameLoaded = true;

        document.dispatchEvent(new CustomEvent('usernameUpdated', {
          detail: { username: usernameInfo.username }
        }));
      } else {
        this.userData._usernameLoaded = true;
      }

      await this._updateNotificationBadge();
    } catch (error) {
      console.error('[Navigation] Failed to fetch username:', error);
      this.userData._usernameLoaded = true;
    }
  }

  async _handleUsernameUpdated(event) {
    if (!event.detail?.username) {
      if (!this.userData) {
        await this._loadUserData();
        this.render();
      }
      return;
    }

    this.userData = { ...this._getExistingUserData(), ...this.userData, name: event.detail.username };
    this._saveUserData(this.userData);

    const userLink = this.element.querySelector('.nav-right a[href="/account.html"]');
    if (userLink && this.userData.name) {
      let usernameElement = userLink.querySelector('.username');
      if (!usernameElement) {
        usernameElement = document.createElement('span');
        usernameElement.className = 'username';
        userLink.appendChild(usernameElement);
      }
      usernameElement.textContent = this.userData.name;
    }

    this.render();
  }

  _handleMembershipDataSynced(event) {
    const syncedUserData = event.detail?.userData || this._getExistingUserData();
    if (!syncedUserData) return;

    this.userData = {
      ...this.userData,
      ...syncedUserData,
      isLoggedIn: true
    };
    this._saveUserData(this.userData);
    this.render();
  }

  registerPage(page) {
    const existingIndex = this.pages.findIndex(p => p.id === page.id);
    if (existingIndex !== -1) {
      this.pages[existingIndex] = { ...this.pages[existingIndex], ...page };
    } else {
      this.pages.push(page);
    }

    if (page.isActive) this.setActivePage(page.id);
    this.render();
    return this;
  }

  setActivePage(pageId) {
    this.pages.forEach(page => page.isActive = false);
    const page = this.pages.find(p => p.id === pageId);
    if (page) {
      page.isActive = true;
      this.activePage = page;
    } else {
      this.activePage = null;
    }
    this.render();
    this.saveState();
    return this;
  }

  getActivePage() {
    return this.activePage;
  }

  setLanguage(language) {
    this.language = normalizeLang(language);
    storage.set('displayLanguage', this.language);
    storage.updateJson('userData', 'language', this.language);
    document.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: this.language } }));
  }

  getLanguage() {
    return this.language || 'en';
  }

  render() {
    this._cleanupEnergyListeners();
    this._cleanupGuestNavCallouts?.();
    this.element.innerHTML = '';

    const sideNav = this._createSideNav();
    const isHangoutPage = this._isHangoutPage();
    const mobileMenuBtn = isHangoutPage ? null : this._createMobileMenuButton();
    const overlay = this._createOverlay();

    const isDesktop = !this._isMobile();
    if (isDesktop && !this._isDrawerMode()) {
      sideNav.classList.add('show');
      overlay.classList.add('show');
      if (mobileMenuBtn) mobileMenuBtn.innerHTML = '<i class="fas fa-bars"></i>';
      document.body.classList.add('side-nav-open');
    }

    this.element.appendChild(sideNav);
    if (mobileMenuBtn) this.element.appendChild(mobileMenuBtn);
    this.element.appendChild(overlay);

    this._addEventListeners();
    void this._applyNavTranslations();
    this._setupGuestNavCallouts();
    this.saveState();
  }

  async _applyNavTranslations() {
    await ensureNavTranslationsLoaded(this.language);
    applyTranslationsToContainer(this.element, 'nav', this.language);
  }

  _cleanupEnergyListeners() {
    if (this._energyCleanups?.length) {
      this._energyCleanups.forEach(fn => fn());
      this._energyCleanups = [];
    }
    this._cleanupSideNavSwipe();
  }

  _cleanupSideNavSwipe() {
    this._sideNavSwipeCleanup?.();
    this._sideNavSwipeCleanup = null;
  }

  _setupGuestNavCallouts() {
    this._cleanupGuestNavCallouts?.();

    const isLoggedIn = !!this.userData?.isLoggedIn;
    if (isLoggedIn) {
      this._cleanupGuestNavCallouts = null;
      return;
    }

    const sideNavContainer = this.element.querySelector('#side-navigation-container');
    if (!sideNavContainer) return;

    const calloutsContainer = document.createElement('div');
    calloutsContainer.className = 'side-nav-guest-callouts';
    calloutsContainer.setAttribute('aria-hidden', 'true');

    /** @type {(targetId: string, extraClass: string) => HTMLDivElement} */
    const createCallout = (targetId, extraClass) => {
      const el = document.createElement('div');
      el.className = `side-nav-guest-callout ${extraClass}`.trim();
      el.dataset.targetId = targetId;

      const arrow = document.createElement('div');
      arrow.className = 'side-nav-guest-callout-arrow';

      const label = document.createElement('div');
      label.className = 'side-nav-guest-callout-label';
      label.textContent = 'FREE';

      el.appendChild(arrow);
      el.appendChild(label);
      return el;
    };

    const callouts = [
      createCallout('theatre', 'callout-theatre'),
      createCallout('create', 'callout-create')
    ];

    callouts.forEach(c => calloutsContainer.appendChild(c));
    document.body.appendChild(calloutsContainer);

    const positionCallouts = () => {
      callouts.forEach(callout => {
        const targetId = callout.dataset.targetId;
        const targetLink = sideNavContainer.querySelector(`[data-page-id="${targetId}"]`);
        if (!targetLink) return;

        const navRect = sideNavContainer.getBoundingClientRect();
        const linkRect = targetLink.getBoundingClientRect();
        const sidebarWidth = navRect.right - navRect.left;
        callout.style.left = `${navRect.right - sidebarWidth * 0.2}px`;
        callout.style.top = `${linkRect.top + linkRect.height / 2}px`;
      });
    };

    const isMobileView = () => window.innerWidth <= 1024;
    const isDrawerMode = () => isMobileView() || document.body.classList.contains('no-side-nav-padding');

    const runPositionAfterTransition = () => {
      let fallback = setTimeout(positionCallouts, 350);
      const onEnd = (e) => {
        if (e.propertyName !== 'transform') return;
        sideNavContainer.removeEventListener('transitionend', onEnd);
        clearTimeout(fallback);
        positionCallouts();
      };
      sideNavContainer.addEventListener('transitionend', onEnd);
    };

    const updateCalloutsVisibility = () => {
      if (!isDrawerMode()) {
        calloutsContainer.classList.remove('side-nav-guest-callouts--hidden');
        positionCallouts();
        return;
      }

      const isOpen = document.body.classList.contains('side-nav-open');
      if (isOpen) {
        calloutsContainer.classList.remove('side-nav-guest-callouts--hidden');
        runPositionAfterTransition();
      } else {
        calloutsContainer.classList.add('side-nav-guest-callouts--hidden');
      }
    };

    updateCalloutsVisibility();

    const mutationObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'class' && m.target === document.body) {
          updateCalloutsVisibility();
          break;
        }
      }
    });
    mutationObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    const handleResize = () => updateCalloutsVisibility();
    window.addEventListener('resize', handleResize);

    const linksSection = sideNavContainer.querySelector('.side-nav-links');
    const resizeObserver = new ResizeObserver(positionCallouts);
    resizeObserver.observe(sideNavContainer);
    sideNavContainer.addEventListener('scroll', positionCallouts);
    linksSection?.addEventListener('scroll', positionCallouts);

    this._cleanupGuestNavCallouts = () => {
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      sideNavContainer.removeEventListener('scroll', positionCallouts);
      linksSection?.removeEventListener('scroll', positionCallouts);
      calloutsContainer.remove();
    };
  }

  _createSideNav() {
    const sideNavContainer = document.createElement('div');
    sideNavContainer.id = 'side-navigation-container';

    const sideNav = document.createElement('nav');
    sideNav.id = 'side-nav';

    // Logo Section with Language Dropdown
    const logoSection = document.createElement('div');
    logoSection.className = 'side-nav-logo';

    const logoRow = document.createElement('div');
    logoRow.className = 'side-nav-logo-row';

    const isLoggedIn = !!this.userData?.isLoggedIn;

    const headerControlsLeft = document.createElement('div');
    headerControlsLeft.className = 'side-nav-header-controls-left';

    const collapseBtn = document.createElement('button');
    collapseBtn.type = 'button';
    collapseBtn.className = 'side-nav-header-btn side-nav-collapse-btn';
    collapseBtn.setAttribute('aria-label', 'Collapse navigation menu');
    collapseBtn.innerHTML = '<i class="fas fa-times" aria-hidden="true"></i>';
    headerControlsLeft.appendChild(collapseBtn);

    logoRow.appendChild(headerControlsLeft);
    logoRow.appendChild(this._createLogo());

    const languageDropdown = createLanguageDropdown({ language: this.language });
    logoRow.appendChild(languageDropdown);

    logoSection.appendChild(logoRow);

    // Navigation Links
    const linksSection = document.createElement('div');
    linksSection.className = 'side-nav-links';
    const items = (/** @type {NavigationOptions & any} */ (this.options)).items || [];

    items.filter(item => {
      if (item.id === 'account' && !isLoggedIn) return false;
      if (item.id === 'season_pass' && !isSfwSite()) return false;
      if (item.id === 'season_pass' && this.userData?.current_tier && this.userData.current_tier !== 'free') return false;
      return true;
    }).forEach(item => {
      const link = document.createElement('a');
      link.href = item.path;
      if (item.isActive || this._isActiveNavPath(item.path)) {
        link.classList.add('active');
        link.setAttribute('aria-current', 'page');
      }
      link.dataset.pageId = item.id;

      if (item.icon) {
        if (item.iconType === 'png') {
          const icon = document.createElement('img');
          icon.src = item.icon;
          icon.alt = '';
          icon.setAttribute('aria-hidden', 'true');
          link.appendChild(icon);
        } else if (item.iconType === 'fontawesome') {
          const icon = document.createElement('i');
          icon.className = item.icon.includes('fa-brands') ? item.icon : `fas ${item.icon}`;
          icon.setAttribute('aria-hidden', 'true');
          link.appendChild(icon);
        } else {
          const icon = document.createElement('i');
          icon.className = `fas ${item.icon}`;
          icon.setAttribute('aria-hidden', 'true');
          link.appendChild(icon);
        }
      }

      const textSpan = document.createElement('span');
      textSpan.dataset.translate = item.title;
      link.appendChild(textSpan);

      if (item.id === 'create_video') {
        const sticker = document.createElement('span');
        sticker.className = 'nav-sticker nav-sticker--new';
        sticker.dataset.translate = 'nav.new_badge';
        sticker.textContent = 'NEW';
        sticker.setAttribute('aria-label', 'New');
        link.appendChild(sticker);
      }

      linksSection.appendChild(link);
    });

    // Action Buttons
    const actionsSection = this._createSideNavActions();

    // User section
    const userSection = this._createSideNavUser();

    sideNav.appendChild(logoSection);
    sideNav.appendChild(actionsSection);
    sideNav.appendChild(linksSection);
    sideNav.appendChild(userSection);

    sideNavContainer.appendChild(sideNav);
    return sideNavContainer;
  }

  _createMobileMenuButton() {
    const btn = document.createElement('button');
    btn.className = 'mobile-menu-btn';
    btn.id = 'hangout-menu-button';
    btn.setAttribute('aria-label', 'Toggle navigation menu');
    btn.innerHTML = '<i class="fas fa-bars"></i>';
    return btn;
  }

  _createOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'side-nav-overlay';
    return overlay;
  }

  _createSideNavActions() {
    const actionsContainer = document.createElement('div');
    actionsContainer.className = 'side-nav-actions';

    const notificationBtn = document.createElement('button');
    notificationBtn.className = 'side-nav-action-btn notification-button';
    notificationBtn.setAttribute('aria-label', 'Notifications');
    notificationBtn.innerHTML = '<i class="fas fa-bell"></i><span class="notification-badge"></span>';
    actionsContainer.appendChild(notificationBtn);

    const feedbackBtn = document.createElement('button');
    feedbackBtn.className = 'side-nav-action-btn feedback-button';
    feedbackBtn.setAttribute('aria-label', 'Pray');
    feedbackBtn.innerHTML = '<i class="fas fa-message"></i>';
    actionsContainer.appendChild(feedbackBtn);

    return actionsContainer;
  }

  _createSideNavUser() {
    const userSection = document.createElement('div');
    userSection.className = 'side-nav-user';

    if (this.userData?.isLoggedIn) {
      const profileLink = document.createElement('a');
      profileLink.href = '/account.html';
      profileLink.className = 'side-nav-user-profile';

      const avatar = document.createElement('img');
      avatar.src = resolveUserAvatarSrc(this.userData);
      avatar.alt = '';
      avatar.className = 'side-nav-user-avatar';
      avatar.referrerPolicy = 'no-referrer';

      const userInfo = document.createElement('div');
      userInfo.className = 'side-nav-user-info';

      if (this.userData.name && this.userData._usernameLoaded !== false) {
        const username = document.createElement('div');
        username.className = 'side-nav-username';
        username.textContent = this.userData.name;
        userInfo.appendChild(username);
      }

      const tier = document.createElement('div');
      tier.className = 'side-nav-user-tier';
      const currentTier = this.userData?.current_tier || 'free';
      tier.textContent = currentTier === 'free' ? 'Free Account' : 'Premium Member';
      userInfo.appendChild(tier);

      profileLink.appendChild(avatar);
      profileLink.appendChild(userInfo);

      const discordUrl = this.options?.discordInviteUrl || this.defaultOptions.discordInviteUrl;
      const discordLink = document.createElement('a');
      discordLink.href = discordUrl;
      discordLink.className = 'side-nav-discord-link';
      discordLink.target = '_blank';
      discordLink.rel = 'noopener noreferrer';
      discordLink.setAttribute('aria-label', 'Discord');
      discordLink.setAttribute('data-translate-aria', 'nav.discord');
      discordLink.innerHTML = '<i class="fab fa-discord" aria-hidden="true"></i>';

      const profileRow = document.createElement('div');
      profileRow.className = 'side-nav-user-row';
      profileRow.appendChild(profileLink);
      profileRow.appendChild(discordLink);
      userSection.appendChild(profileRow);

      if (shouldShowEnergyBar(this.userData)) {
        const { element, cleanup } = createSideNavEnergy();
        this._energyCleanups.push(cleanup);
        userSection.appendChild(element);
      }
    } else {
      const authContainer = document.createElement('div');
      authContainer.className = 'side-nav-auth';

      const createFreeBtn = document.createElement('button');
      createFreeBtn.className = 'side-nav-auth-btn primary create-free-account-button';
      createFreeBtn.setAttribute('data-translate', 'nav.create_free_account');
      createFreeBtn.textContent = 'Create a free account';
      authContainer.appendChild(createFreeBtn);

      const loginBtn = document.createElement('button');
      loginBtn.className = 'side-nav-auth-btn secondary login-button';
      loginBtn.setAttribute('data-translate', 'nav.login');
      loginBtn.textContent = 'Login';
      authContainer.appendChild(loginBtn);

      userSection.appendChild(authContainer);
    }

    return userSection;
  }

  _createLeftSection() {
    const leftSection = document.createElement('div');
    leftSection.className = 'nav-left';

    if (!this._isMobile()) {
      leftSection.appendChild(this._createLogo());
    }
    return leftSection;
  }

  _createLogo() {
    const logoLink = document.createElement('a');
    logoLink.href = '/rankings.html';
    logoLink.className = 'logo-link';

    const textLogo = document.createElement('div');
    textLogo.className = 'text-logo';

    const name = document.createElement('span');
    name.className = 'text-logo-name';
    name.textContent = 'Kissandra';

    const tld = document.createElement('span');
    tld.className = 'text-logo-tld';
    tld.textContent = '.ai';

    const beta = document.createElement('span');
    beta.className = 'text-logo-beta';
    beta.textContent = 'beta';

    textLogo.appendChild(name);
    textLogo.appendChild(tld);
    textLogo.appendChild(beta);
    logoLink.appendChild(textLogo);

    return logoLink;
  }

  _createCenterSection() {
    const centerSection = document.createElement('div');
    centerSection.className = 'nav-center';

    const items = (/** @type {NavigationOptions & any} */ (this.options)).items || [];
    const isLoggedIn = !!this.userData?.isLoggedIn;

    items.filter(item => {
      if (item.id === 'account' && !isLoggedIn) return false;
      if (item.id === 'season_pass' && !isSfwSite()) return false;
      if (item.id === 'season_pass' && this.userData?.current_tier && this.userData.current_tier !== 'free') return false;
      return true;
    }).forEach(item => {
      const link = document.createElement('a');
      link.href = item.path;
      if (item.isActive || this._isActiveNavPath(item.path)) {
        link.classList.add('active');
        link.setAttribute('aria-current', 'page');
      }
      link.dataset.pageId = item.id;

      if (item.icon) {
        if (item.iconType === 'png') {
          const icon = document.createElement('img');
          icon.src = item.icon;
          icon.alt = '';
          icon.setAttribute('aria-hidden', 'true');
          icon.className = 'nav-icon-png';
          link.appendChild(icon);
        } else {
          const icon = document.createElement('i');
          icon.className = `fas ${item.icon}`;
          icon.setAttribute('aria-hidden', 'true');
          link.appendChild(icon);
        }
      }

      const textSpan = document.createElement('span');
      textSpan.dataset.translate = item.title;
      link.appendChild(textSpan);

      centerSection.appendChild(link);
    });

    return centerSection;
  }

  _createRightSection() {
    const rightSection = document.createElement('div');
    rightSection.className = 'nav-right';

    const isMobile = this._isMobile();

    if (this.userData?.isLoggedIn) {
      rightSection.appendChild(createNotificationIcon());

      if (!isMobile) rightSection.appendChild(createFeedbackButton());

      if (shouldShowEnergyBar(this.userData)) {
        const { element, cleanup } = createEnergyBar();
        this._energyCleanups.push(cleanup);
        rightSection.appendChild(element);
      }

      rightSection.appendChild(this._createUserLink());
    } else {
      if (!isMobile) rightSection.appendChild(createFeedbackButton());

      const createFreeBtn = document.createElement('a');
      createFreeBtn.href = '#';
      createFreeBtn.className = 'create-free-account-button';
      createFreeBtn.setAttribute('data-translate', 'nav.create_free_account');
      createFreeBtn.textContent = 'Create a free account';
      rightSection.appendChild(createFreeBtn);

      const loginButton = document.createElement('a');
      loginButton.className = 'login-button';
      loginButton.dataset.translate = 'nav.login';
      rightSection.appendChild(loginButton);
    }

    rightSection.appendChild(createLanguageDropdown({ language: this.language }));
    return rightSection;
  }

  _createUserLink() {
    const userLink = document.createElement('a');
    userLink.href = '/account.html';

    const avatar = document.createElement('img');
    avatar.src = resolveUserAvatarSrc(this.userData);
    avatar.alt = '';
    avatar.className = 'nav-profile-pic';
    avatar.referrerPolicy = 'no-referrer';

    userLink.appendChild(avatar);

    if (this.userData.name && this.userData._usernameLoaded !== false) {
      const username = document.createElement('span');
      username.className = 'username nav-username';
      username.textContent = this.userData.name;
      userLink.appendChild(username);
    }

    return userLink;
  }

  _addEventListeners() {
    const loginButton = this.element.querySelector('.login-button');
    const createFreeAccountButton = this.element.querySelector('.create-free-account-button');
    const trialOfferButton = this.element.querySelector('.trial-offer-nav-button');
    const userLink = this.element.querySelector('.side-nav-user-profile');
    const notificationButton = this.element.querySelector('.notification-button');
    const feedbackButton = this.element.querySelector('.feedback-button');
    const dropdownButton = this.element.querySelector('.dropdown .lang-dropbtn');
    const dropdownContent = this.element.querySelector('.dropdown-content');
    const internalMobileMenuBtn = this.element.querySelector('.mobile-menu-btn');
    const hangoutMenuBtn = document.getElementById('hangout-menu-button');
    const sideNavCollapseBtn = this.element.querySelector('.side-nav-collapse-btn');
    const overlay = this.element.querySelector('.side-nav-overlay');
    const sideNav = this.element.querySelector('#side-navigation-container');
    const menuButtons = [internalMobileMenuBtn, hangoutMenuBtn].filter(Boolean);

    // Mobile menu toggle
    if (menuButtons.length > 0 && overlay && sideNav) {
      const openMenu = () => this._setMenuOpenState(true);
      const toggleMenu = () => this._setMenuOpenState(!this._isMenuOpen());

      if (internalMobileMenuBtn) {
        internalMobileMenuBtn.addEventListener('click', toggleMenu);
        internalMobileMenuBtn.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          toggleMenu();
        });
      }

      if (hangoutMenuBtn) {
        hangoutMenuBtn.addEventListener('click', openMenu);
        hangoutMenuBtn.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          openMenu();
        });
      }

      if (sideNavCollapseBtn) {
        sideNavCollapseBtn.addEventListener('click', () => this._setMenuOpenState(false));
      }

      overlay.addEventListener('click', () => {
        this._setMenuOpenState(false);
      });

      this.element.querySelectorAll('.side-nav-links a').forEach(link => {
        link.addEventListener('click', () => {
          if (window.innerWidth <= 1024) {
            this._setMenuOpenState(false);
          }
        });
      });

      this._setMenuOpenState(sideNav.classList.contains('show'));

      this._sideNavSwipeCleanup?.();
      this._sideNavSwipeCleanup = setupSideNavSwipe({
        getIsDrawerMode: () => this._isDrawerMode(),
        getIsOpen: () => this._isMenuOpen(),
        setOpen: (isOpen) => this._setMenuOpenState(isOpen),
        sideNav: /** @type {HTMLElement} */ (sideNav),
        overlay: /** @type {HTMLElement} */ (overlay),
      });
    }

    this.element.querySelectorAll('.side-nav-links a').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        if (isGoogleOneTapAuthPending()) return;
        const pageId = item.dataset.pageId;
        const page = (/** @type {NavigationOptions & any} */ (this.options)).items?.find(p => p.id === pageId);
        if (!page) return;
        window.location.href = page.path;
      });
    });

    loginButton?.addEventListener('click', async (e) => {
      e.preventDefault();
      await this._storeHangoutSessionIfNeeded();
      loginModalController.show();
    });

    if (createFreeAccountButton) {
      const openFree = async (e) => {
        e.preventDefault();
        await this._storeHangoutSessionIfNeeded();
        try {
          const { showFreeAccountModal } = await import('../../js/components/FreeAccountModal.js');
          await showFreeAccountModal();
        } catch (err) {
          console.error('Failed to open free account modal:', err);
        }
      };
      createFreeAccountButton.addEventListener('click', openFree);
      createFreeAccountButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFree(e); }
      });

      createFreeAccountButton.addEventListener('mouseenter', () => {
        createFreeAccountButton.style.setProperty('background-color', 'rgba(255, 107, 157, 0.2)', 'important');
      });
      createFreeAccountButton.addEventListener('mouseleave', () => {
        createFreeAccountButton.style.setProperty('background-color', 'rgba(255, 107, 157, 0.1)', 'important');
      });
    }

    if (userLink && this.userData?.isLoggedIn) {
      userLink.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = '/account.html';
      });
    }

    if (notificationButton) {
      notificationButton.addEventListener('click', this._handleNotificationClick.bind(this));
      notificationButton.addEventListener('keydown', this._handleNotificationKeydown.bind(this));
      notificationButton.addEventListener('mouseenter', () => {
        notificationButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
      });
      notificationButton.addEventListener('mouseleave', () => {
        notificationButton.style.backgroundColor = 'transparent';
      });
    }

    if (feedbackButton) {
      const openFeedback = (e) => {
        handleFeedbackClick(e);
      };
      feedbackButton.addEventListener('click', openFeedback);
      feedbackButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFeedback(e); }
      });
      feedbackButton.addEventListener('mouseenter', () => {
        feedbackButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        const t = feedbackButton.querySelector('.feedback-tooltip');
        if (t) { t.style.opacity = '1'; t.style.visibility = 'visible'; }
      });
      feedbackButton.addEventListener('mouseleave', () => {
        feedbackButton.style.backgroundColor = 'transparent';
        const t = feedbackButton.querySelector('.feedback-tooltip');
        if (t) { t.style.opacity = '0'; t.style.visibility = 'hidden'; }
      });
    }

    if (dropdownButton && dropdownContent) {
      dropdownButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropdownContent.classList.toggle('show');
      });

      const closeDropdown = (e) => {
        if (!dropdownButton.contains(/** @type {Node} */ (e.target)) && !dropdownContent.contains(/** @type {Node} */ (e.target))) {
          dropdownContent.classList.remove('show');
        }
      };
      document.addEventListener('click', closeDropdown);
      document.addEventListener('touchstart', closeDropdown, { passive: true });
    }

    this.element.querySelectorAll('.dropdown-content button[data-lang]').forEach(option => {
      option.addEventListener('click', (e) => {
        this._handleLanguageChange(e);
      });
    });

    if (trialOfferButton) {
      addTrialButtonEventListeners(trialOfferButton);
    }

    const opts = /** @type {NavigationOptions & any} */ (this.options);
    if (opts.events) {
      opts.events.subscribe('user:updated', this._handleUserUpdated.bind(this));
      opts.events.subscribe('language:changed', this._handleLanguageChanged.bind(this));
    }
  }

  _setupMobileAutoHideNav() {
    // Swipe gestures are wired in _addEventListeners via setupSideNavSwipe
  }

  _isDrawerMode() {
    return window.innerWidth <= 1024 || document.body.classList.contains('no-side-nav-padding');
  }

  _isMenuOpen() {
    const sideNav = this.element.querySelector('#side-navigation-container');
    return sideNav?.classList.contains('show') ?? false;
  }

  _setMenuOpenState(isOpen) {
    const sideNav = this.element.querySelector('#side-navigation-container');
    const overlay = this.element.querySelector('.side-nav-overlay');
    if (!sideNav || !overlay) return;

    sideNav.classList.toggle('show', isOpen);
    overlay.classList.toggle('show', isOpen);
    document.body.classList.toggle('side-nav-open', isOpen);

    const internalMobileMenuBtn = this.element.querySelector('.mobile-menu-btn');
    if (internalMobileMenuBtn) {
      internalMobileMenuBtn.innerHTML = isOpen ? '<i class="fas fa-times"></i>' : '<i class="fas fa-bars"></i>';
      internalMobileMenuBtn.setAttribute('aria-expanded', String(isOpen));
    }

    const hangoutMenuBtn = document.getElementById('hangout-menu-button');
    if (hangoutMenuBtn) {
      hangoutMenuBtn.innerHTML = '<i class="fas fa-bars"></i>';
      hangoutMenuBtn.setAttribute('aria-expanded', 'false');
    }
  }

  async _handleNotificationClick(e) {
    e.preventDefault();
    await showNotificationPanel({
      navElement: this.element,
      navigateToScenario,
      navigateToCharacter,
      navigateToCreateVideo,
      navigateToMyGallery
    });
  }

  _handleNotificationKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._handleNotificationClick(e);
    }
  }

  updateNotificationCount(count) {
    updateNotificationCount(this.element, count);
  }

  async _updateNotificationBadge() {
    const count = await fetchNotificationCount({ isLoggedIn: !!this.userData?.isLoggedIn });
    this.updateNotificationCount(count);
  }

  async _handleLanguageChange(e) {
    e.preventDefault();
    e.stopPropagation();
    const language = e.currentTarget.dataset.lang;
    if (!language) return;

    this.setLanguage(language);

    const nextSeoPath = seoPathForLanguage(window.location.pathname, language);
    if (nextSeoPath) {
      const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
      if (nextSeoPath !== currentPath) {
        window.location.assign(nextSeoPath);
        return;
      }
    }

    const dropbtn = this.element.querySelector('.lang-dropbtn');
    if (dropbtn) {
      const currentFlag = dropbtn.querySelector('.flag-icon');
      if (currentFlag) {
        currentFlag.remove();
        const newFlag = _createFlagElementImpl(getLanguageFlag(language));
        newFlag.setAttribute('aria-label', getLanguageName(language));
        dropbtn.insertBefore(newFlag, dropbtn.firstChild);
      }

      const langCode = dropbtn.querySelector('.lang-code');
      if (langCode) langCode.textContent = language.toUpperCase();
    }

    const dropdownContent = this.element.querySelector('.dropdown-content');
    if (dropdownContent) dropdownContent.classList.remove('show');

    if (this.userData?.isLoggedIn) {
      const token = await tokenManager.getValidAccessToken();
      if (token) {
        fetch(`${USER_WORKER_URL}/language`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ language })
        })
          .then(response => response.json())
          .then(() => {
            this.userData = { ...this.userData, language };
            this._saveUserData(this.userData);
          })
          .catch(error => console.error('[Navigation] Failed to update language:', error));
      }
    }
  }

  _handleUserUpdated(user) {
    this.user = user;
    this.render();
  }

  _handleLanguageChanged(lang) {
    this.language = lang;
    this.render();
  }

  _isMobile() {
    const isMobileScreen = window.innerWidth <= 1024;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return isMobileScreen || (isMobileUA && isTouchDevice);
  }

  _isLandscapeMobile() {
    return window.innerWidth <= 1024 && window.innerHeight < window.innerWidth;
  }

  /**
   * @param {Element} element
   * @param {NavigationOptions & any} [options]
   */
  constructor(element, options = {}) {
    super(element, options);
    const opts = /** @type {NavigationOptions & any} */ (this.options);
    this.pages = opts.items || [];
    this.activePage = this.pages.find(page => page.isActive) || null;
    this._energyCleanups = [];
    this._sideNavSwipeCleanup = null;
    this.loadState();
  }

  loadState() {
    try {
      const savedState = storage.get('displayLanguage') || 'en';
      this.language = savedState;
      if (this.activePage && this.activePage.id) this.setActivePage(this.activePage.id);
    } catch {
      // Ignore invalid state
    }
  }

  saveState() {
    storage.set('displayLanguage', this.language);
  }

  // Legacy instance-method wrappers kept for external callers
  _createFlagElement(symbolId) { return _createFlagElementImpl(symbolId, 'flag-icon'); }
  _getLanguageFlag(code) { return getLanguageFlag(code); }
  _getLanguageFlags() { return LANGUAGE_DATA.flags; }
  _getLanguageData() { return LANGUAGE_DATA; }
  _getLanguageName(code) { return getLanguageName(code); }
}

// Re-export so existing consumers importing from Navigation.js keep working
export { createFlagElement } from './flags.js';

// Factory expected by pageLoader
export function createNavigation(element, options) {
  return new Navigation(element, /** @type {NavigationOptions & any} */ (options));
}

// keep default for flexibility
export default Navigation;
